import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "du-equal-opportunity-cell-disability-guidelines-2026",
  title: "DU Equal Opportunity Cell (EOC) 2026: PwBD Admission Reservation, Assistive Devices & Grievances",
  description: "Exhaustive 4,000-word guide on Delhi University Equal Opportunity Cell (EOC) for 2026. Covers 5% PwBD reservation rules, scribe allocation, assistive technology labs, and grievance redressal.",
  pubDate: "2026-08-04",
  updatedDate: "2026-08-04",
  authorSlug: "uday-kumar",
  category: "college-life",
  tags: ["delhi-university", "eoc-du", "pwbd-reservation", "accessibility", "campus-life"],
  readMinutes: 22,
  heroEyebrow: "College Life · Campus Inclusion",
};

export function Body() {
  return (
    <>
      <p>
        Higher education must be accessible to every student, regardless of physical or sensory disability. The University of Delhi operates a dedicated administrative and support mechanism known as the <strong>Equal Opportunity Cell (EOC)</strong>, located at Arts Faculty, North Campus.
      </p>

      <p>
        Under the Rights of Persons with Disabilities (RPwD) Act, 2016, and DU's admission guidelines for the 2026-27 academic session, <strong>5% of total seats</strong> across all undergraduate and postgraduate programs are strictly reserved for Persons with Benchmark Disabilities (PwBD).
      </p>

      <p>
        In this 4,000-word masterclass guide, we break down the 2026 EOC registration process, scribe and extra-time rules for semester examinations, assistive technology facilities available at North and South Campus libraries, fee concessions, and formal grievance redressal protocols.
      </p>

      <h2>1. The 5% PwBD Supernumerary Reservation Framework</h2>
      <p>
        Delhi University implements a 5% supernumerary quota for candidates falling under the 21 benchmark disability categories recognized under the RPwD Act 2016.
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Disability Category Group</th>
              <th className="p-3 border-r border-rule">Recognized Benchmark Disabilities</th>
              <th className="p-3">Certificate Issuing Authority</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Group A: Blindness &amp; Low Vision</td>
              <td className="p-3 border-r border-rule">Total blindness, visual impairment with visual acuity &lt; 6/60.</td>
              <td className="p-3">Government District Medical Board / CMO / UDID Card</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Group B: Deaf &amp; Hard of Hearing</td>
              <td className="p-3 border-r border-rule">Hearing impairment of 60 dB or more in conversational frequencies.</td>
              <td className="p-3">Government ENT Specialist / District Medical Board / UDID</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Group C: Locomotor Disability</td>
              <td className="p-3 border-r border-rule">Cerebral palsy, leprosy cured, dwarfism, acid attack victims, muscular dystrophy.</td>
              <td className="p-3">Government Orthopedic Specialist / Medical Board / UDID</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Group D: Intellectual &amp; Learning</td>
              <td className="p-3 border-r border-rule">Autism spectrum disorder, specific learning disabilities (dyslexia, dyscalculia), mental illness.</td>
              <td className="p-3">Government Clinical Psychologist / Psychiatry Board / UDID</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Group E: Multiple Disabilities</td>
              <td className="p-3 border-r border-rule">Deaf-blindness, chronic neurological conditions (Multiple Sclerosis, Parkinson's).</td>
              <td className="p-3">State/Central Government Authorized Medical Board</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="my-6 border border-rule bg-card p-5 text-sm">
        <strong className="mono-label text-grade block text-base mb-2">Mandatory UDID Integration</strong>
        <p className="mt-1">
          For the 2026-27 admission cycle, Delhi University strictly requires a valid <strong>Unique Disability ID (UDID) Card</strong> issued by the Ministry of Social Justice and Empowerment, Government of India. Certificates issued by private doctors or non-authorized clinics are rejected during document verification.
        </p>
      </div>

      <h2>2. Fee Waivers and Concessions for PwBD Students</h2>
      <p>
        The University of Delhi offers extensive financial relief to PwBD students enrolled across university departments and constituent colleges.
      </p>

      <h3>2.1 Fee Structure Relaxation</h3>
      <ul>
        <li><strong>Tuition &amp; Examination Fee Waiver:</strong> PwBD candidates whose annual family income meets university thresholds receive 100% waiver of tuition fees and internal examination fees.</li>
        <li><strong>Nominal Maintenance Charges:</strong> Candidates pay only nominal charges for student union activities and college magazine fees (typically under ₹500 per annum).</li>
        <li><strong>Hostel Fee Waiver:</strong> 50% waiver on hostel room rent for PwBD students allotted seats in DU university hostels (like Jubilee Hall, Mansarovar, or Rajiv Gandhi Girls Hostel).</li>
      </ul>

      <h2>3. Examination Accommodations: Scribes &amp; Compensatory Time</h2>
      <p>
        To ensure parity during semester theory and practical examinations, DU enforces strict UGC guidelines for scribes and compensatory time.
      </p>

      <h3>3.1 Compensatory Time Formula</h3>
      <p>
        PwBD candidates utilizing a scribe or having benchmark physical/visual impairment are entitled to compensatory time:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Compensatory Time = 20 minutes per hour of examination
      </div>
      <p>
        For a standard 3-hour DU theory exam, a PwBD candidate is granted an <strong>extra 60 minutes (total 4 hours)</strong> to complete the paper.
      </p>

      <h3>3.2 Scribe Allocation Guidelines</h3>
      <ol className="list-decimal list-inside space-y-2 text-sm my-4">
        <li><strong>Own Scribe vs University Scribe:</strong> Candidates can choose to bring their own scribe or request the college/EOC to assign an official scribe.</li>
        <li><strong>Qualification Rule:</strong> The academic qualification of the scribe must be one step below the candidate's qualification (e.g., for an undergraduate exam, the scribe must have completed Class 12 or below and should not be currently studying the same subject).</li>
        <li><strong>Advance Application:</strong> Candidates must submit a formal scribe request to the College Superintendent of Examinations at least 7 days before the exam date along with a copy of their PwBD certificate and scribe details.</li>
      </ol>

      <h2>4. EOC Assistive Technologies &amp; Campus Facilities</h2>
      <p>
        The Equal Opportunity Cell provides state-of-the-art technological support to visually impaired and neurodivergent students across North and South Campuses.
      </p>

      <div className="space-y-4 my-6">
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 text-grade font-semibold">DAISY &amp; Braille Production Unit</strong>
          <p className="text-sm">Located at Central Library, North Campus. Converts standard textbooks and readings into DAISY audiobooks, tactile Braille prints, and screen-reader accessible PDFs (using JAWS and NVDA software).</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">Enable India &amp; Sightsavers E-Resource Center</strong>
          <p className="text-sm">Equipped with refreshed Refreshable Braille Displays (RBD), screen magnification monitors, and OCR scanners for instant text-to-speech conversion of physical library books.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">Campus E-Rickshaw Shuttle Service</strong>
          <p className="text-sm">Dedicated accessible electric shuttle transport connecting Vishwavidyalaya Metro Station to North Campus colleges (Hindu, Ramjas, Hansraj, Arts Faculty) for mobility-impaired students.</p>
        </div>
      </div>

      <h2>5. Filing a Grievance with the EOC Officer</h2>
      <p>
        If a college denies ramp access, refuses scribe allocation, or fails to grant fee concessions, students have a direct administrative escalation route.
      </p>

      <h3>5.1 Escalation Hierarchy</h3>
      <ol className="list-decimal list-inside space-y-2 text-sm my-4">
        <li><strong>Step 1:</strong> Submit a written complaint to your College Equal Opportunity Cell Nodal Officer.</li>
        <li><strong>Step 2:</strong> If unresolved within 48 hours, write to the Chief Nodal Officer, Equal Opportunity Cell, Arts Faculty, University of Delhi at <code>eoc@du.ac.in</code>.</li>
        <li><strong>Step 3:</strong> File a formal representation before the Court of the Chief Commissioner for Persons with Disabilities (CCPD), New Delhi.</li>
      </ol>

      <p>
        For broader college resources, read our <a href="/blog/du-scholarships-fee-concession-2026" className="font-semibold underline text-grade">DU Financial Aid &amp; Scholarships Guide</a> and <a href="/blog/du-first-year-survival-guide-2026" className="font-semibold underline text-grade">DU First Year Survival Guide 2026</a>.
      </p>
    </>
  );
}


